import { GaleriaBig } from "@/galeria/Galeria";
import { useConfigContext } from "@/state/configContext";
import { router } from "@/main";
import { GET } from "@/state/api";
import { POST } from "@/state/server";
import { type Foto, type Temporada } from "@/state/types";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useRef, useState, type Ref } from "react";
import { z } from "zod";

const SearchOptionsZod = z.object({
  temporada_id: z.coerce.number().optional(),
  fecha: z.string().optional(),
  fotografo: z.coerce.number().optional(),
  lugar_id: z.coerce.number().optional(),
});
type SearchOptions = z.infer<typeof SearchOptionsZod>;
const SearchZod = z.union([
  SearchOptionsZod,
  z.object({ subida_id: z.coerce.number() }),
]);

export const Route = createFileRoute("/admin/fotos")({
  component: RouteComponent,
  validateSearch: SearchZod,
  loader: (params) => {
    const search = params.location.search as z.infer<typeof SearchZod>;
    if ("subida_id" in search) {
      return GET("/admin/buscar", {
        q: { "buscador[subida_id]": search.subida_id },
      });
    }
    if (search.fecha && (search.fotografo || search.lugar_id)) {
      return GET("/admin/buscar", {
        q: {
          "buscador[estricto]": true,
          ...Object.fromEntries(
            Object.entries(search).map(([k, v]) => [`buscador[${k}]`, v])
          ),
        },
      });
    }
    return null;
  },
});

function RouteComponent() {
  const search = Route.useSearch();
  const config = useConfigContext();

  const temporada =
    config.temporadas[("temporada_id" in search && search.temporada_id) || 0] ||
    config.temporadas[config.temporada];
  const fotos = Route.useLoaderData()?.galeria.fotos;

  const [selected, setSelected] = useState<Record<number, boolean>>({});

  const icons = ({ foto }: { foto: Foto }) => (
    <>
      <input
        type="checkbox"
        checked={!!selected[foto.i]}
        onChange={() =>
          setSelected({ ...selected, [foto.i]: !selected[foto.i] })
        }
      />
      <FontAwesomeIcon icon={faPencil} />
      <br />
      {foto.borrada && "BORRADA"}
    </>
  );

  return (
    <>
      {"subida_id" in search ? (
        <div>Subida {search.subida_id}</div>
      ) : (
        <Buscador search={search} />
      )}
      {fotos && (
        <>
          <Operaciones
            fotos={fotos}
            selected={selected}
            setSelected={setSelected}
            temporada={temporada}
            onModify={() => router.invalidate()}
          />
          <GaleriaBig fotos={fotos} icons={icons} />
        </>
      )}
    </>
  );
}

function Buscador({ search }: { search: SearchOptions }) {
  const fechaSelect = useRef<HTMLInputElement>(null),
    fotografoSelect = useRef<HTMLSelectElement>(null),
    lugarSelect = useRef<HTMLSelectElement>(null);
  const router = useRouter();

  const config = useConfigContext();

  const temporada =
    config.temporadas[("temporada_id" in search && search.temporada_id) || 0] ||
    config.temporadas[config.temporada];

  return (
    <div>
      <select
        value={temporada.id}
        onChange={(e) =>
          router.navigate({
            to: "/admin/fotos",
            search: { temporada_id: Number(e.target.value) },
          })
        }
      >
        {Object.values(config.temporadas)
          .sort((a, b) => b.prioridad - a.prioridad)
          .map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
      </select>{" "}
      <input ref={fechaSelect} type="date" defaultValue={search.fecha} />{" "}
      <FotografoSelect
        temporada={temporada}
        ref={fotografoSelect}
        defaultValue={search.fotografo}
      />{" "}
      <LugarSelect
        temporada={temporada}
        ref={lugarSelect}
        defaultValue={search.lugar_id}
      />{" "}
      <button
        onClick={() => {
          if (
            fechaSelect.current?.value &&
            (fotografoSelect.current?.value || lugarSelect.current?.value)
          ) {
            router.navigate({
              to: Route.fullPath,
              search: Object.fromEntries(
                Object.entries({
                  temporada_id: temporada.id,
                  fecha: fechaSelect.current?.value,
                  fotografo: Number(fotografoSelect.current?.value),
                  lugar_id: Number(lugarSelect.current?.value),
                }).filter(([, v]) => v)
              ),
            });
          } else {
            alert("Elija criterio de busqueda");
          }
        }}
      >
        BUSCAR
      </button>
    </div>
  );
}

function Operaciones({
  fotos,
  selected,
  setSelected,
  temporada,
  onModify,
}: {
  fotos: Foto[];
  selected: Record<number, boolean>;
  setSelected: (v: Record<number, boolean>) => unknown;
  temporada: Temporada;
  onModify: () => unknown;
}) {
  const fechaSelect = useRef<HTMLInputElement>(null),
    fotografoSelect = useRef<HTMLSelectElement>(null),
    lugarSelect = useRef<HTMLSelectElement>(null),
    promoSelect = useRef<HTMLSelectElement>(null);

  const seleccionadas = Object.entries(selected).flatMap(([id, v]) =>
    v ? id : []
  );

  function onChange() {
    setSelected({});
    onModify();
  }

  return (
    <>
      <div>
        <button
          onClick={() =>
            setSelected(
              Object.fromEntries(
                fotos.map((f) => [f.i, !selected[fotos[0]?.i]])
              )
            )
          }
        >
          {`${selected[fotos[0]?.i] ? "DES" : ""}MARCAR TODAS`}
        </button>
      </div>
      <div>
        <button
          onClick={() =>
            POST("/admin/fotos/activar", {
              fotos: seleccionadas,
              activo: false,
            }).then(onChange)
          }
        >
          Borrar {seleccionadas.length} fotos
        </button>
      </div>
      <div>
        <button
          onClick={() =>
            POST("/admin/fotos/activar", {
              fotos: seleccionadas,
              activo: true,
            }).then(onChange)
          }
        >
          Reactivar {seleccionadas.length} fotos
        </button>
      </div>
      <div>
        Modificar {seleccionadas.length} fotos, fotografo{" "}
        <FotografoSelect ref={fotografoSelect} temporada={temporada} />, lugar{" "}
        <LugarSelect temporada={temporada} ref={lugarSelect} />, fecha{" "}
        <input type="date" ref={fechaSelect} />, promo:
        <PromoSelect temporada={temporada} ref={promoSelect} />{" "}
        <button
          onClick={() => {
            if (confirm("Ojo que esto no tiene vuelta atrás, seguro?")) {
              POST("/admin/fotos/mover", {
                fotos: seleccionadas,
                datos: {
                  fecha: fechaSelect.current?.value,
                  fotografo_id: fotografoSelect.current?.value,
                  lugar_id: lugarSelect.current?.value,
                  promo_id: promoSelect.current?.value,
                },
              }).then(onChange);
            }
          }}
        >
          MODIFICAR
        </button>
      </div>
    </>
  );
}

function FotografoSelect({
  temporada,
  ref,
  defaultValue,
}: {
  temporada: Temporada;
  ref: Ref<HTMLSelectElement>;
  defaultValue?: number;
}) {
  const config = useConfigContext();

  return (
    <select ref={ref} defaultValue={defaultValue}>
      <option />
      {temporada.fotografos
        .filter((x) => x[0] > 0)
        .sort((a, b) => a[0] - b[0])
        .map((x) => (
          <option key={x[1]} value={x[1]}>
            {x[0]}.- {config.fotografos[x[1]].nombre}
          </option>
        ))}
    </select>
  );
}

function LugarSelect({
  temporada,
  ref,
  defaultValue,
}: {
  temporada: Temporada;
  ref: Ref<HTMLSelectElement>;
  defaultValue?: number;
}) {
  return (
    <select ref={ref} defaultValue={defaultValue}>
      <option />
      {temporada.lugares.map((x) => (
        <option key={x.id} value={x.id}>
          {x.name}
        </option>
      ))}
    </select>
  );
}

function PromoSelect({
  temporada,
  ref,
  defaultValue,
}: {
  temporada: Temporada;
  ref: Ref<HTMLSelectElement>;
  defaultValue?: number;
}) {
  return (
    <select ref={ref} defaultValue={defaultValue}>
      <option />
      <option value={-1}>Anular promos</option>
      {temporada.promos?.map((x) => (
        <option key={x.id} value={x.id}>
          {x.descripcion}
        </option>
      ))}
    </select>
  );
}
